import React, { useContext, useEffect } from 'react';
import { CircularProgress, Grid } from '@material-ui/core';
import { SmartModuleType, SmartModule } from '@moderepo/mode-internal-apis';
import { fetchAllHomeSmartModules, selectHomeSmartModulesByType, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { capitalizeAll, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { VideosListHiddenFields, VideosListingContainer } from './VideosListingContainer';
import { useRitzTranslation } from '../../utils/customHooks';



/**
 * Required/optional props for this container
 */
export interface VideoSmartModulesContainerProps {
    readonly projectId: number;
    readonly userId?: number | undefined;
    readonly homeId: number;
    readonly itemsPerPage?: number | undefined;
    readonly hiddenFields?: VideosListHiddenFields | undefined;
}


/**
 * This container display TABLES of home videos, 1 for each video smart module.
 */
export const VideoSmartModulesContainer: React.FC<VideoSmartModulesContainerProps> = (
    props: VideoSmartModulesContainerProps,
) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);


    // Get all the Video smart modules from the data state
    const videoSmartModules = selectHomeSmartModulesByType(appDataState, props.homeId, SmartModuleType.VIDEO);


    /**
     * This useEffect is used for fetching all the smart modules this home has
     */
    useEffect(() => {
        if (!videoSmartModules) {
            appDataDispatch(fetchAllHomeSmartModules(props.homeId)).catch(() => {
                // ignore error
            });
        }
    }, [appDataDispatch, props.homeId, videoSmartModules]);



    return (
        <>
            {videoSmartModules && (
                <Grid container spacing={3}>
                    {videoSmartModules.map((module: SmartModule) => {
                        // For each video smart modules, create a VideosListingContainer to show the videos for that smart module
                        return (
                            <Grid item xs={12} key={module.id}>
                                <VideosListingContainer
                                    projectId={props.projectId}
                                    userId={props.userId}
                                    homeId={props.homeId}
                                    videoSmartModuleId={module.id}
                                    hiddenFields={props.hiddenFields}
                                    itemsPerPage={props.itemsPerPage}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {videoSmartModules && videoSmartModules.length === 0 && (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.video', { count: 2 })),
                    })}
                />
            )}

            {!videoSmartModules && (
                <CircularProgress />
            )}
        </>
    );
};
